import { __awaiter } from "tslib";
import React from 'react';
import { Navigate } from 'react-router';
import { createDrawerConfig } from '@/navigation/drawer';
import authorized from '@/routing/ui/authorized/authorized';
import { InitialSelectionPage } from '@/search-for-retail/generic/page-context-selection';
import { PageIdentifier, Role, Routing } from '@/constants';
import { isGlobalModelType } from '@/search-for-retail/models';
import Managers from './search-for-retail.managers';
import { hasEnrichAccessOnly } from '@/utils/environment';
import { GbLoading } from '@/components/gb-loading';
import { SearchForRetailUtilities } from '@/search-for-retail/utilities';
import { FeatureFlag } from './feature-flags';
import { useLocalization } from '@/localization';
import { useSessionAreaName } from '@/platform-services/session';
export var SearchForRetailRoutes;
(function (SearchForRetailRoutes) {
    const getCategoryRoutes = (routes) => routes.reduce((routesArray, route) => {
        var _a;
        const subroutes = ((_a = route.subroutes) === null || _a === void 0 ? void 0 : _a.length) && !route.disabled ? getCategoryRoutes(route.subroutes) : [];
        return [...routesArray, ...(route.disabled ? [] : [route]), ...subroutes];
    }, []);
    const getDynamicRoutes = (featureFlagsService) => {
        const isNewSearchPerformanceDashboard = featureFlagsService.isFeatureEnabled(FeatureFlag.EnableNewSearchPerformanceDashboard);
        const s4rDrawerSections = createDrawerConfig({
            isNewSearchPerformanceDashboard,
        }).sections.filter((section) => !(section === null || section === void 0 ? void 0 : section.plugin));
        const routesArray = s4rDrawerSections.reduce((acc, section) => {
            let sectionRoutes = [];
            if (!section.disabled) {
                section.categories.forEach((category) => {
                    const categoryRoutes = getCategoryRoutes(category.routes);
                    sectionRoutes = [...sectionRoutes, ...categoryRoutes];
                });
            }
            return [...acc, ...sectionRoutes];
        }, []);
        return routesArray.map((route) => {
            const subPath = route.path === 'analytics'
                ? ':collection?/:area?/:dashboardTitle?'
                : route.path === 'areas'
                    ? '_create?'
                    : ':collection?/:area?/_create?';
            return {
                id: route.name,
                path: route.path,
                hydrateFallbackElement: <GbLoading />,
                children: [
                    {
                        path: subPath,
                        lazy: () => __awaiter(this, void 0, void 0, function* () {
                            const mod = yield Managers[route.name]();
                            const AuthorizedComponent = authorized(route.roles)(mod.default);
                            const Wrapper = () => {
                                const activeAreaName = useSessionAreaName();
                                if (isGlobalModelType(route.name) || activeAreaName) {
                                    return <AuthorizedComponent />;
                                }
                                return (<InitialSelectionPage titleMessageKey={route.label} selectionMessageKey="SELECT_COLLECTION_AND_AREA_PAGE_TEXT"/>);
                            };
                            return {
                                Component: Wrapper,
                            };
                        }),
                    },
                ],
            };
        });
    };
    const getAuditLogLazyComponent = () => __awaiter(this, void 0, void 0, function* () {
        const mod = yield Managers[PageIdentifier.AUDIT_LOG]();
        const AuthorizedComponent = authorized([Role.MERCHANDISING])(mod.default);
        const Wrapper = () => <AuthorizedComponent />;
        return { Component: Wrapper };
    });
    const DefaultElement = () => {
        const { formatMessage: t } = useLocalization();
        return (<p>{t({ key: 'NO_PAGE_FOUND' })}</p>);
    };
    const getAllRoutes = (featureFlagsService) => [
        {
            index: true,
            hydrateFallbackElement: <GbLoading />,
            lazy: () => __awaiter(this, void 0, void 0, function* () {
                const HomeDashboardMod = yield Managers[PageIdentifier.HOME_DASHBOARDS]();
                const HomeDashboardComponent = HomeDashboardMod.default;
                const Wrapper = () => {
                    if (hasEnrichAccessOnly) {
                        return <Navigate to={SearchForRetailUtilities.navigateTo()}/>;
                    }
                    return <HomeDashboardComponent />;
                };
                return { Component: Wrapper };
            }),
        },
        ...getDynamicRoutes(featureFlagsService),
        {
            path: Routing.ROUTE_PATHS[PageIdentifier.AUDIT_LOG],
            children: [
                {
                    index: true,
                    hydrateFallbackElement: <GbLoading />,
                    lazy: getAuditLogLazyComponent,
                },
                {
                    path: ':collectionName',
                    hydrateFallbackElement: <GbLoading />,
                    lazy: getAuditLogLazyComponent,
                },
            ],
        },
        {
            path: '*',
            element: <DefaultElement />,
        },
    ];
    SearchForRetailRoutes.getRoutes = getAllRoutes;
})(SearchForRetailRoutes || (SearchForRetailRoutes = {}));
